import { inject, Injectable } from '@angular/core';
import { CustomerState, CustomerStore } from './customer.store';
import { CustomerQuery } from './customer.query';
import { CompanyService, ContactModel, CustomerService } from '@railmybox/api-user';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { arrayAdd, arrayUpsert } from '@datorama/akita';

@Injectable({ providedIn: 'root' })
export class CustomerStoreService {
  private store = inject(CustomerStore);
  private query = inject(CustomerQuery);
  private customerService = inject(CustomerService);
  private readonly companyService = inject(CompanyService);

  initializeCustomerInfo(): Observable<CustomerState> {
    return this.customerService.getCustomer().pipe(
      tap((customer) => {
        this.store.update({ ...customer });
      })
    );
  }

  updateCustomerState(customer: Partial<CustomerState>): Observable<CustomerState> {
    return this.customerService.updateCustomer(customer).pipe(tap((customerInfo) => this.store.update({ ...customerInfo })));
  }

  getCompanyContactList(): Observable<ContactModel[]> {
    this.companyService
      .listCompanyContacts()
      .pipe(tap((contacts: ContactModel[]) => this.store.update({ contacts })))
      .subscribe();
    return this.query.select('contacts');
  }

  inviteContact(mail: string) {
    return this.companyService
      .createInvitation(mail)
      .toPromise()
      .then((contact) => {
        this.store.update((state) => {
          const contacts = state.contacts;

          return {
            ...state,
            contacts: arrayAdd(contacts, contact),
          };
        });
      });
  }

  resendInvitation(invitationKey: string) {
    return this.companyService.resendInvitation(invitationKey).toPromise();
  }

  acceptInvitation(invitationKey: string): Observable<any> {
    return this.companyService.acceptInvitation(invitationKey).pipe(tap(() => this.initializeCustomerInfo().toPromise()));
  }

  updateContact(id: string, role: string) {
    return this.companyService.updateContact(id, role).toPromise();
  }

  /**
   * Retrieves the CustomerStoreQuery instance
   */
  getCustomerQuery(): CustomerQuery {
    return this.query;
  }

  getCustomerLegalTypeCollection(): Observable<{ label: string; value: string }[]> {
    return this.customerService.listLegalTypes().pipe(
      map((legalTypes) => {
        return legalTypes.map((legalType) => {
          return { label: legalType.value, value: legalType.id };
        });
      })
    );
  }

  getCustomerProfileType(): Observable<{ label: string; value: string }[]> {
    return this.customerService.listProfileTypes().pipe(
      map((profileTypes) => {
        return profileTypes.map((profileType) => {
          return { label: profileType.value, value: profileType.id };
        });
      })
    );
  }

  getCustomer(key: string) {
    return this.customerService.showCustomer(key).toPromise();
  }

  async activateContact(key: string): Promise<any> {
    return this.companyService
      .activateContact(key)
      .toPromise()
      .then(() => {
        this.store.update((state) => {
          return {
            ...state,
            contacts: arrayUpsert(this.query.getContacts(), key, { active: true }),
          };
        });
      });
  }

  async deactivateContact(key: string): Promise<any> {
    return this.companyService
      .deactivateContact(key)
      .toPromise()
      .then(() => {
        this.store.update((state) => {
          return {
            ...state,
            contacts: arrayUpsert(this.query.getContacts(), key, { active: false }),
          };
        });
      });
  }

  async deleteInvitation(invitationId: string) {
    return this.companyService
      .deleteInvitation(invitationId)
      .toPromise()
      .then(() => {
        this.store.update((state) => {
          const contacts = state.contacts.filter((el) => el.invitationId !== invitationId);
          return {
            ...state,
            contacts: contacts,
          };
        });
      });
  }
}
